import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, UserX, Check, CalendarCheck, Mail, Lightbulb, UserPlus } from 'lucide-react';

// Task type interfaces
export interface UnsetAvailabilityTask {
  teamId: number;
  teamName: string;
  count: number;
  nextMatchDate: string;
}

export interface IncompleteLineupTask {
  teamId: number;
  teamName: string;
  count: number;
  nextMatchDate: string;
}

export interface LineupAvailabilityConflictTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
}

export interface CaptainLineupAvailabilityConflictTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
  conflictCount: number;
}

export interface UnconfirmedLineupTask {
  matchId: number;
  opponentName: string;
  matchDate: string;
  teamName: string;
  unconfirmedCount: number;
}

export interface PendingInvitationTask {
  count: number;
  invitedAt: string;
}

export interface OnboardingTask {
  task_id: string;
  title: string;
  description: string;
  action_url: string;
  status: string;
}

export interface DashboardTasks {
  unsetAvailabilityTasks: UnsetAvailabilityTask[];
  incompleteLineupTasks: IncompleteLineupTask[];
  lineupAvailabilityConflictTasks: LineupAvailabilityConflictTask[];
  captainLineupConflictTasks: CaptainLineupAvailabilityConflictTask[];
  unconfirmedLineupTasks: UnconfirmedLineupTask[];
  pendingInvitationTasks: PendingInvitationTask[];
}

interface DashboardTasksListProps {
  dashboardTasks: DashboardTasks;
  onboardingTasks?: OnboardingTask[];
  teams?: Array<{ id: number; name: string }>;
  onNavigateToStrategize?: (teamId: number) => void;
  onNavigateToTeamManagement?: () => void;
  onDismissOnboardingTask?: (taskId: string) => void;
  dismissingTaskId?: string | null;
  emptyMessage?: string;
  maxTasks?: number; // Max number of tasks to show initially (for expand functionality)
  showExpandButton?: boolean; // Whether to show expand/collapse button
  onToggleExpand?: () => void; // Callback when expand button is clicked
  isExpanded?: boolean; // Whether tasks are currently expanded
}

type CombinedTask =
  | (LineupAvailabilityConflictTask & { type: 'conflict' })
  | (CaptainLineupAvailabilityConflictTask & { type: 'captain-conflict' })
  | (UnconfirmedLineupTask & { type: 'unconfirmed-lineup' })
  | (UnsetAvailabilityTask & { type: 'availability' })
  | (IncompleteLineupTask & { type: 'lineup' })
  | (PendingInvitationTask & { type: 'invitation' })
  | (OnboardingTask & { type: 'onboarding' });

const DashboardTasksList: React.FC<DashboardTasksListProps> = ({
  dashboardTasks,
  onboardingTasks = [],
  teams = [],
  onNavigateToStrategize,
  onNavigateToTeamManagement,
  onDismissOnboardingTask,
  dismissingTaskId = null,
  emptyMessage = "You don't have any tasks at the moment.",
  maxTasks,
  showExpandButton = false,
  onToggleExpand,
  isExpanded = false
}) => {
  const navigate = useNavigate();

  // Create combined array of all tasks with type information
  const allTasks: CombinedTask[] = [
    ...dashboardTasks.lineupAvailabilityConflictTasks.map(task => ({ ...task, type: 'conflict' as const })),
    ...dashboardTasks.captainLineupConflictTasks.map(task => ({ ...task, type: 'captain-conflict' as const })),
    ...dashboardTasks.unconfirmedLineupTasks.map(task => ({ ...task, type: 'unconfirmed-lineup' as const })),
    ...dashboardTasks.unsetAvailabilityTasks.map(task => ({ ...task, type: 'availability' as const })),
    ...dashboardTasks.incompleteLineupTasks.map(task => ({ ...task, type: 'lineup' as const })),
    ...dashboardTasks.pendingInvitationTasks.map(task => ({ ...task, type: 'invitation' as const })),
    ...onboardingTasks.map(task => ({ ...task, type: 'onboarding' as const }))
  ];

  // Sort by date (earliest first - most urgent)
  // Onboarding tasks go first (highest priority), then by date
  allTasks.sort((a, b) => {
    // Onboarding tasks always come first
    if (a.type === 'onboarding' && b.type !== 'onboarding') return -1;
    if (a.type !== 'onboarding' && b.type === 'onboarding') return 1;

    // For onboarding tasks, maintain their order
    if (a.type === 'onboarding' && b.type === 'onboarding') return 0;

    // For other tasks, sort by date
    let dateA: number;
    if (a.type === 'conflict' || a.type === 'captain-conflict' || a.type === 'unconfirmed-lineup') {
      dateA = new Date(a.matchDate).getTime();
    } else if (a.type === 'invitation') {
      dateA = new Date(a.invitedAt).getTime();
    } else if (a.type === 'availability' || a.type === 'lineup') {
      dateA = new Date(a.nextMatchDate).getTime();
    } else {
      // Onboarding tasks already handled above, but TypeScript needs this
      dateA = 0;
    }
    
    let dateB: number;
    if (b.type === 'conflict' || b.type === 'captain-conflict' || b.type === 'unconfirmed-lineup') {
      dateB = new Date(b.matchDate).getTime();
    } else if (b.type === 'invitation') {
      dateB = new Date(b.invitedAt).getTime();
    } else if (b.type === 'availability' || b.type === 'lineup') {
      dateB = new Date(b.nextMatchDate).getTime();
    } else {
      // Onboarding tasks already handled above, but TypeScript needs this
      dateB = 0;
    }
    return dateA - dateB;
  });

  if (allTasks.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 md:p-12 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-gray-100">All caught up!</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  // Get tasks to display (first maxTasks if not expanded, all if expanded)
  const tasksToShow = (maxTasks && !isExpanded) ? allTasks.slice(0, maxTasks) : allTasks;
  const totalTaskCount = allTasks.length;

  return (
    <>
      <div className="space-y-3">
        {tasksToShow.map((task) => {
        if (task.type === 'conflict') {
          return (
            <Link
              key={`conflict-${task.matchId}`}
              to={`/matches/${task.matchId}`}
              className="block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-yellow-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      Lineup conflict: vs {task.opponentName}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      You're in the lineup but not marked as available
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        } else if (task.type === 'captain-conflict') {
          // Find teamId from teamName
          const team = teams.find(t => t.name === task.teamName);
          const teamId = team?.id;

          const handleClick = () => {
            if (onNavigateToStrategize && teamId) {
              onNavigateToStrategize(teamId);
            } else if (teamId) {
              navigate(`/teams/strategize?team=${teamId}`);
            }
          };

          return (
            <button
              key={`captain-conflict-${task.matchId}`}
              onClick={handleClick}
              disabled={!teamId}
              className="w-full text-left block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <UserX className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      Player conflict: vs {task.opponentName}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {task.conflictCount} {task.conflictCount === 1 ? 'player' : 'players'} in lineup but not marked as available
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        } else if (task.type === 'unconfirmed-lineup') {
          return (
            <Link
              key={`unconfirmed-lineup-${task.matchId}`}
              to={`/matches/${task.matchId}`}
              className="block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-blue-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      Confirm lineup: vs {task.opponentName}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {task.unconfirmedCount} {task.unconfirmedCount === 1 ? 'position' : 'positions'} need confirmation
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        } else if (task.type === 'availability') {
          return (
            <Link
              key={`availability-${task.teamId}`}
              to={`/availability?match=upcoming&team=${task.teamId}&status=not_set`}
              className="block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-orange-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <CalendarCheck className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      Set match availability for {task.teamName}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {task.count} {task.count === 1 ? 'unset availability' : 'unset availabilities'}
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        } else if (task.type === 'invitation') {
          const handleClick = () => {
            if (onNavigateToTeamManagement) {
              onNavigateToTeamManagement();
            } else {
              navigate('/teams/strategize?showTeamSelector=true');
            }
          };

          return (
            <button
              key={`invitation-${task.count}`}
              onClick={handleClick}
              className="w-full text-left block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-purple-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Mail className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      You have {task.count} pending captain invitation{task.count === 1 ? '' : 's'}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Review and respond to your invitations
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        } else if (task.type === 'onboarding') {
          return (
            <div
              key={`onboarding-${task.task_id}`}
              className="group relative block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-teal-500"
              onClick={() => navigate(task.action_url)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <Lightbulb className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      {task.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {task.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {onDismissOnboardingTask && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismissOnboardingTask(task.task_id);
                      }}
                      disabled={dismissingTaskId === task.task_id}
                      className="flex-shrink-0 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors disabled:opacity-50 opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                      title="Dismiss this task"
                    >
                      Dismiss
                    </button>
                  )}
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          );
        } else {
          // Incomplete lineup task
          const handleClick = () => {
            if (onNavigateToStrategize) {
              onNavigateToStrategize(task.teamId);
            } else {
              navigate(`/teams/strategize?team=${task.teamId}`);
            }
          };

          return (
            <button
              key={`lineup-${task.teamId}`}
              onClick={handleClick}
              className="w-full text-left block bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-blue-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <UserPlus className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="flex flex-col">
                    <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100">
                      Set lineup for {task.teamName}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {task.count} {task.count === 1 ? 'incomplete lineup' : 'incomplete lineups'}
                    </p>
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          );
        }
        })}
      </div>
      
      {showExpandButton && maxTasks && totalTaskCount > maxTasks && (
        <div className="mt-4 text-center">
          <button
            onClick={onToggleExpand}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {isExpanded 
              ? 'Show less' 
              : `View all ${totalTaskCount} tasks`}
          </button>
        </div>
      )}
    </>
  );
};

export default DashboardTasksList;

